import { useState, useRef, useEffect } from 'react'
import { useNotifications } from '@/contexts/NotificationContext'
import NotificationItem from './NotificationItem'
import NotificationBadge from '../ui/NotificationBadge'
import { CheckCheck, Bell } from 'lucide-react'
import EmptyState from '../ui/EmptyState'

export default function NotificationCenter({ variant = 'header' }: { variant?: 'sidebar' | 'header' }) {
    const [isOpen, setIsOpen] = useState(false)
    const { notifications, unreadCount, markAsRead, markAllAsRead, loading } = useNotifications()
    const dropdownRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false)
            }
        }

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [isOpen])


    // Show only UNREAD notifications in the dropdown
    const unreadNotifications = notifications.filter(n => !n.read)
    const recentNotifications = unreadNotifications.slice(0, 10)

    return (
        <div className="relative" ref={dropdownRef}>
            <NotificationBadge
                count={unreadCount}
                onClick={() => setIsOpen(!isOpen)}
                variant={variant}
            />

            {isOpen && (
                <div className={`absolute mt-2 w-96 glass-card p-0 shadow-2xl shadow-primary/20 z-[9999] animate-in fade-in zoom-in duration-200 ${variant === 'sidebar'
                    ? 'left-full top-0 ml-4'
                    : 'right-0'
                    }`}>
                    {/* Header */}
                    <div className="p-4 border-b border-primary/10 flex items-center justify-between">
                        <h3 className="text-sm font-black text-primary uppercase tracking-wider">
                            Notificações
                        </h3>
                        {unreadCount > 0 && (
                            <button
                                onClick={markAllAsRead}
                                className="text-xs font-bold text-secondary hover:text-primary transition-colors flex items-center gap-1"
                            >
                                <CheckCheck className="w-4 h-4" />
                                Marcar todas como lidas
                            </button>
                        )}
                    </div>

                    {/* Notifications List */}
                    <div className="max-h-96 overflow-y-auto">
                        {loading ? (
                            <div className="p-8 text-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                            </div>
                        ) : recentNotifications.length === 0 ? (
                            <div className="p-8">
                                <EmptyState
                                    icon={Bell}
                                    title="Nenhuma notificação"
                                    description="Você está em dia! Não há notificações no momento."
                                />
                            </div>
                        ) : (
                            <div className="divide-y divide-primary/10">
                                {recentNotifications.map((notification) => (
                                    <NotificationItem
                                        key={notification.id}
                                        notification={notification}
                                        onMarkAsRead={markAsRead}
                                    />
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    {unreadNotifications.length > 10 && (
                        <div className="p-3 border-t border-primary/10 text-center">
                            <a href="/communication" className="text-xs font-bold text-primary hover:text-secondary transition-colors uppercase tracking-wider">
                                Ver todas as notificações
                            </a>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
